// Client-side mirror of ember/src/utils/bookingValidation.ts#isWithin24Hours.
// Used purely to show the "you'll still be charged" warning on the confirmation step
// before the user submits an amendment. The server remains the source of truth — it
// re-runs the same check and flags the booking with wasChargedOnAmendment=true.
//
// Returns true iff the booking (or any non-excluded recurring occurrence) sits inside
// the 24h window from now. Elevated users bypass the check (they're exempt from the
// charge-on-amendment policy).

import { utcDayKey } from "@/lib/calendarDay";
import type { Booking } from "@/types/Booking";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const hhmmToHoursMinutes = (hhmm: string): { hours: number; minutes: number } => {
  const padded = hhmm.padStart(4, "0");
  return {
    hours: parseInt(padded.slice(0, 2), 10),
    minutes: parseInt(padded.slice(2, 4), 10),
  };
};

// Day-identity compare via the UTC calendar day (the booking's `date` is UTC-midnight of the
// centre's day) — a viewer-local getFullYear/getMonth/getDate compare shifts the day for anyone
// off the centre's TZ.
const sameDay = (a: Date, b: Date): boolean => utcDayKey(a) === utcDayKey(b);

export interface Within24HoursResult {
  /** True iff at least one non-excluded occurrence sits in the (now, now+24h] window. */
  isWithin24Hours: boolean;
  /** The first occurrence that falls inside the window (for human-readable messaging). */
  occurrence?: Date;
}

export function isBookingWithin24Hours(
  booking: Pick<Booking, "date" | "startTime" | "recurrence">,
  options: { isElevatedUser?: boolean; now?: Date } = {},
): Within24HoursResult {
  if (options.isElevatedUser) return { isWithin24Hours: false };

  const now = options.now ?? new Date();
  const windowEnd = new Date(now.getTime() + MS_PER_DAY);
  // R16 — the charge window is "within 24 hours of the occurrence start", which is symmetric:
  // a booking starting in the next 24h AND one that started in the last 24h (in progress / just
  // passed) both still bill on amendment. The previous `dt >= now` lower bound wrongly excluded
  // the in-progress case, letting a member shorten a running booking with no warning.
  const windowStart = new Date(now.getTime() - MS_PER_DAY);
  const { hours, minutes } = hhmmToHoursMinutes(booking.startTime);

  const exceptionSet = new Set(
    (booking.recurrence?.exceptions ?? []).map((d) => utcDayKey(new Date(d))),
  );

  // Build the occurrence's start INSTANT on `occDate`'s UTC calendar day at the booking's start
  // time. `occDate` is always a UTC-day-anchored Date (the booking's UTC-midnight `date`, or a
  // value stepped from it below via setUTCDate/setUTCMonth) — Date.UTC keeps the day fixed
  // regardless of viewer TZ, unlike the previous local `setHours`.
  const occurrenceWithin = (occDate: Date): Date | null => {
    if (exceptionSet.has(utcDayKey(occDate))) return null;
    const dt = new Date(
      Date.UTC(occDate.getUTCFullYear(), occDate.getUTCMonth(), occDate.getUTCDate(), hours, minutes, 0, 0),
    );
    if (dt >= windowStart && dt <= windowEnd) return dt;
    return null;
  };

  // Base / non-recurring case.
  const baseDate = booking.date instanceof Date ? booking.date : new Date(booking.date);
  const baseHit = occurrenceWithin(baseDate);
  if (baseHit) return { isWithin24Hours: true, occurrence: baseHit };

  // Recurring — walk forward until past the window or hit the rule's terminus.
  const rec = booking.recurrence;
  if (!rec || !rec.frequency) return { isWithin24Hours: false };

  const interval = rec.interval ?? 1;
  const until = rec.until ? new Date(rec.until) : null;
  const maxOccurrences = rec.count && rec.count > 0 ? rec.count : 520; // ~10y weekly safety
  let current = new Date(baseDate);

  for (let i = 1; i < maxOccurrences; i++) {
    if (rec.frequency === "weekly") {
      current = new Date(current);
      current.setUTCDate(current.getUTCDate() + 7 * interval);
    } else if (rec.frequency === "monthly") {
      current = new Date(current);
      current.setUTCMonth(current.getUTCMonth() + interval);
    } else {
      break;
    }

    if (until && current > until) break;
    const candidateDateTime = new Date(
      Date.UTC(current.getUTCFullYear(), current.getUTCMonth(), current.getUTCDate(), hours, minutes, 0, 0),
    );
    if (candidateDateTime > windowEnd) break;

    const hit = occurrenceWithin(current);
    if (hit) return { isWithin24Hours: true, occurrence: hit };

    // Same-day duplicates: stop if we've stopped advancing for any reason.
    if (sameDay(current, windowEnd) && candidateDateTime > windowEnd) break;
  }

  return { isWithin24Hours: false };
}
